/**
 * Source-faithful starting slots for the original v1 courses.
 *
 * The v1 runtime walks backwards over its already-smoothed/rotated source
 * samples.  This helper deliberately works in those source units: callers
 * that consume the v2 metre-scaled layout can scale x/y/lateral and use the
 * returned sample index to recover the corresponding world distance.  It is
 * intentionally detached from the v2 track registry so authored and saved
 * courses keep their existing grid behaviour.
 */

export const CLASSIC_STARTING_GRID_SIZE = 20;
export const CLASSIC_GRID_WALKBACK_START = 34;
export const CLASSIC_GRID_WALKBACK_ROW = 42;
export const CLASSIC_GRID_WALKBACK_COLUMN = 15;
export const CLASSIC_GRID_LATERAL_RATIO = 0.34;

export type ClassicSourcePoint = readonly [x: number, y: number];

export interface ClassicStartingGridSlot {
  /** Zero-based slot in the source's alternating two-column grid. */
  readonly index: number;
  readonly row: number;
  readonly column: 0 | 1;
  /** Source sample selected by the exact backwards walk. */
  readonly sampleIndex: number;
  /** Source-space position; the second coordinate becomes v2 world z. */
  readonly x: number;
  readonly y: number;
  /** Source tangent heading, measured with atan2(dy, dx), as in v1. */
  readonly heading: number;
  /** Signed source-space offset from the centreline. */
  readonly lateral: number;
  /** Requested source-space walkback before sample quantisation. */
  readonly targetDistance: number;
  /** Accumulated source-space walkback after the exact sample walk. */
  readonly walkedDistance: number;
}

function assertSourcePoints(points: readonly ClassicSourcePoint[]): number {
  if (!Array.isArray(points) || points.length === 0) throw new Error("Classic starting grid needs source samples");
  for (const point of points) {
    if (!Array.isArray(point) || point.length < 2 || !Number.isFinite(point[0]) || !Number.isFinite(point[1])) {
      throw new Error("Classic starting grid samples must be finite x/y pairs");
    }
  }

  let totalLength = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    const segmentLength = Math.hypot(next[0] - current[0], next[1] - current[1]);
    if (!Number.isFinite(segmentLength)) throw new Error("Classic starting grid source geometry is too large");
    totalLength += segmentLength;
    if (!Number.isFinite(totalLength)) throw new Error("Classic starting grid source length is too large");
  }
  if (!(totalLength > 0)) throw new Error("Classic starting grid needs non-zero source length");
  return totalLength;
}

/**
 * Reproduce shared/sim.js `buildTrack(...).gridSlots` exactly.
 *
 * In particular, the loop does not interpolate between samples or replace
 * the accumulated walkback with a nominal distance.  It advances to the
 * previous sample until the requested target is reached, then computes the
 * heading from the +/-2 sample tangent and applies the signed width*0.34
 * lateral offset.
 */
export function buildClassicStartingGrid(
  points: readonly ClassicSourcePoint[],
  width: number,
): readonly ClassicStartingGridSlot[] {
  if (!Number.isFinite(width) || width <= 0) throw new Error("Classic starting grid width must be positive");
  const totalSourceLength = assertSourcePoints(points);

  const sampleCount = points.length;
  let sampleIndex = 0;
  let walkedDistance = 0;
  let walkSteps = 0;
  // Real v1 tracks are thousands of source units long.  Keep malformed
  // near-zero layouts from spending unbounded time wrapping around their
  // samples while retaining a deterministic source walk for valid layouts.
  const maxWalkSteps = Math.max(100_000, Math.ceil(CLASSIC_GRID_WALKBACK_START + 9 * CLASSIC_GRID_WALKBACK_ROW + CLASSIC_GRID_WALKBACK_COLUMN) * sampleCount * 4);
  const slots: ClassicStartingGridSlot[] = [];

  for (let index = 0; index < CLASSIC_STARTING_GRID_SIZE; index += 1) {
    const row = Math.floor(index / 2);
    const column = (index % 2) as 0 | 1;
    const targetDistance = CLASSIC_GRID_WALKBACK_START
      + row * CLASSIC_GRID_WALKBACK_ROW
      + column * CLASSIC_GRID_WALKBACK_COLUMN;

    // This is intentionally the source walk, including its strict `<`
    // comparison and sample-quantised overshoot.
    while (walkedDistance < targetDistance) {
      if (walkSteps >= maxWalkSteps) {
        throw new Error(`Classic starting grid source length is too small for a safe walk (${totalSourceLength})`);
      }
      const previousIndex = (sampleIndex - 1 + sampleCount) % sampleCount;
      const current = points[sampleIndex];
      const previous = points[previousIndex];
      walkedDistance += Math.hypot(current[0] - previous[0], current[1] - previous[1]);
      sampleIndex = previousIndex;
      walkSteps += 1;
    }

    const before = points[(sampleIndex - 2 + sampleCount) % sampleCount];
    const after = points[(sampleIndex + 2) % sampleCount];
    const heading = Math.atan2(after[1] - before[1], after[0] - before[0]);
    const lateral = (column ? 1 : -1) * width * CLASSIC_GRID_LATERAL_RATIO;
    const point = points[sampleIndex];
    slots.push(Object.freeze({
      index,
      row,
      column,
      sampleIndex,
      x: point[0] - Math.sin(heading) * lateral,
      y: point[1] + Math.cos(heading) * lateral,
      heading,
      lateral,
      targetDistance,
      walkedDistance,
    }));
  }

  return Object.freeze(slots);
}
